import React from 'react';
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonProgressBar,
} from '@ionic/react';
import { closeOutline, arrowBackOutline } from 'ionicons/icons';
import { useProfileWizardContext } from '@/contexts/ProfileWizardContext';

interface ProfileWizardHeaderProps {
  progress: number;
  /** When set, shown as the visible centered toolbar title for this step. */
  title?: string;
  /** When set, a Back button appears in the start slot; omit on the entry step. */
  onBack?: () => void;
}

// The single shared toolbar + progress bar every Profile Wizard step renders.
// Mirrors the Share Wizard's ShareWizardHeader, minus the action-name sub-strip:
// the Profile Wizard has no action context, so the header is title + progress
// only. Back is optional (per step); Close ("X") is always present and exits the
// whole wizard — there is no in-flight-send state to warn about (unlike Share).
const ProfileWizardHeader: React.FC<ProfileWizardHeaderProps> = ({
  progress,
  title,
  onBack,
}) => {
  const { exitWizard } = useProfileWizardContext();

  return (
    <IonHeader>
      <IonToolbar>
        <IonButtons slot="start">
          {onBack && (
            <IonButton
              onClick={onBack}
              aria-label="Back"
              data-testid="profile-wizard-back"
            >
              <IonIcon
                slot="icon-only"
                icon={arrowBackOutline}
                aria-hidden="true"
              />
            </IonButton>
          )}
        </IonButtons>
        <IonTitle className={title ? undefined : 'ion-hide-accessible'}>
          {title ?? 'Your profile'}
        </IonTitle>
        <IonButtons slot="end">
          <IonButton
            onClick={() => {
              void exitWizard();
            }}
            aria-label="Close"
            data-testid="profile-wizard-close"
          >
            <IonIcon slot="icon-only" icon={closeOutline} aria-hidden="true" />
          </IonButton>
        </IonButtons>
      </IonToolbar>
      <IonProgressBar
        value={progress}
        color="success"
        data-testid="profile-wizard-progress"
      />
    </IonHeader>
  );
};

export default ProfileWizardHeader;
